import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import analyticsService from "../../services/analyticsService";
import "./FunnelAnalytics.css";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const FunnelAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await analyticsService.getFunnelAnalytics({});
      if (response.success) {
        setData(response.data);
      } else {
        setError(response.error || "Failed to fetch funnel analytics");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(err.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="funnel-analytics-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="funnel-analytics">
        <div className="error-container">
          <span className="material-symbols-outlined">error</span>
          <p>{error}</p>
          <button onClick={fetchData} className="retry-button">
            <span className="material-symbols-outlined">refresh</span>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { summary, funnel, timeAnalysis } = data;

  // Chart data
  const funnelChartData = {
    labels: funnel.map((step) => step.eventName),
    datasets: [{
      label: "Users",
      data: funnel.map((step) => step.uniqueUsers),
      backgroundColor: [
        "rgba(33, 150, 243, 0.8)",
        "rgba(76, 175, 80, 0.8)",
        "rgba(255, 152, 0, 0.8)",
        "rgba(156, 39, 176, 0.8)",
        "rgba(244, 67, 54, 0.8)",
        "rgba(0, 188, 212, 0.8)",
      ],
      borderColor: [
        "rgba(33, 150, 243, 1)",
        "rgba(76, 175, 80, 1)",
        "rgba(255, 152, 0, 1)",
        "rgba(156, 39, 176, 1)",
        "rgba(244, 67, 54, 1)",
        "rgba(0, 188, 212, 1)",
      ],
      borderWidth: 2,
      borderRadius: 8,
    }],
  };

  const conversionChartData = {
    labels: ["Completed", "Dropped"],
    datasets: [{
      data: [summary.completedUsers, summary.totalUsers - summary.completedUsers],
      backgroundColor: [
        "rgba(76, 175, 80, 0.8)",
        "rgba(244, 67, 54, 0.8)",
      ],
      borderColor: [
        "rgba(76, 175, 80, 1)",
        "rgba(244, 67, 54, 1)",
      ],
      borderWidth: 3,
    }],
  };

  return (
    <div className="funnel-analytics">
      {/* Header Section */}
      <div className="funnel-header">
        <div className="header-content">
          <div className="header-icon-wrapper">
            <span className="material-symbols-outlined">trending_up</span>
          </div>
          <div className="header-text">
            <h1>Conversion Funnel Analysis</h1>
            <p className="header-subtitle">Track user journey through conversion steps</p>
          </div>
        </div>
        <button onClick={fetchData} className="refresh-button">
          <span className="material-symbols-outlined">refresh</span>
          Refresh Data
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-grid">
        <motion.div
          className="summary-card total-users"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="card-icon-wrapper">
            <div className="card-icon">
              <span className="material-symbols-outlined">group</span>
            </div>
          </div>
          <div className="card-info">
            <h3>Total Users</h3>
            <p className="metric-value">{summary.totalUsers.toLocaleString()}</p>
            <span className="metric-label">Started funnel</span>
          </div>
        </motion.div>

        <motion.div
          className="summary-card completed-users"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="card-icon-wrapper">
            <div className="card-icon">
              <span className="material-symbols-outlined">verified</span>
            </div>
          </div>
          <div className="card-info">
            <h3>Completed</h3>
            <p className="metric-value">{summary.completedUsers.toLocaleString()}</p>
            <span className="metric-label">Finished funnel</span>
          </div>
        </motion.div>

        <motion.div
          className="summary-card conversion-rate"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="card-icon-wrapper">
            <div className="card-icon">
              <span className="material-symbols-outlined">percent</span>
            </div>
          </div>
          <div className="card-info">
            <h3>Conversion Rate</h3>
            <p className="metric-value">{summary.overallConversionRate}%</p>
            <span className="metric-label">Overall success rate</span>
          </div>
        </motion.div>

        <motion.div
          className="summary-card funnel-steps"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="card-icon-wrapper">
            <div className="card-icon">
              <span className="material-symbols-outlined">stairs</span>
            </div>
          </div>
          <div className="card-info">
            <h3>Funnel Steps</h3>
            <p className="metric-value">{summary.funnelSteps}</p>
            <span className="metric-label">Total steps</span>
          </div>
        </motion.div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <motion.div
          className="chart-card"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div className="chart-header">
            <h3>
              <span className="material-symbols-outlined">bar_chart</span>
              Funnel by Step
            </h3>
          </div>
          <div className="chart-container">
            <Bar
              data={funnelChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                    padding: 12,
                    titleFont: { size: 14, weight: "600" },
                    bodyFont: { size: 13 },
                    cornerRadius: 8,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: {
                      color: "rgba(0, 0, 0, 0.05)",
                    },
                    ticks: {
                      font: { size: 12 },
                      color: "#666",
                    },
                  },
                  x: {
                    grid: {
                      display: false,
                    },
                    ticks: {
                      font: { size: 12 },
                      color: "#666",
                    },
                  },
                },
              }}
            />
          </div>
        </motion.div>

        <motion.div
          className="chart-card conversion-chart"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6 }}
        >
          <div className="chart-header">
            <h3>
              <span className="material-symbols-outlined">donut_large</span>
              Conversion Overview
            </h3>
          </div>
          <div className="chart-container doughnut-container">
            <Doughnut
              data={conversionChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "bottom",
                    labels: {
                      padding: 20,
                      font: { size: 13, weight: "600" },
                      usePointStyle: true,
                    },
                  },
                  tooltip: {
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                    padding: 12,
                    cornerRadius: 8,
                  },
                },
              }}
            />
          </div>
        </motion.div>
      </div>

      {/* Funnel Visualization */}
      <motion.div
        className="funnel-visualization-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <div className="card-header">
          <h3>
            <span className="material-symbols-outlined">filter_alt</span>
            Conversion Funnel
          </h3>
        </div>
        <div className="funnel-container">
          {funnel.map((step, index) => {
            const width = (step.uniqueUsers / funnel[0].uniqueUsers) * 100;
            const isFirst = index === 0;
            const isLast = index === funnel.length - 1;
            
            return (
              <motion.div
                key={index}
                className="funnel-step-wrapper"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
              >
                <div className="funnel-step-info">
                  <div className="step-badge">
                    <span className="step-number">{step.step}</span>
                  </div>
                  <div className="step-details">
                    <h4 className="step-name">{step.eventName}</h4>
                    <div className="step-stats">
                      <span className="stat-item">
                        <span className="material-symbols-outlined">people</span>
                        {step.uniqueUsers.toLocaleString()} users
                      </span>
                      {!isFirst && (
                        <span className="stat-item drop-off-stat">
                          <span className="material-symbols-outlined">trending_down</span>
                          {step.dropOffRate}% drop-off
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="funnel-bar-container">
                  <div
                    className={`funnel-bar ${isFirst ? "first" : ""} ${isLast ? "last" : ""}`}
                    style={{ width: `${width}%` }}
                  >
                    <div className="bar-fill">
                      <div className="bar-shine"></div>
                    </div>
                    <div className="conversion-badge">
                      {step.conversionRate}%
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Tables Section */}
      <div className="tables-section">
        <motion.div
          className="table-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          <div className="card-header">
            <h3>
              <span className="material-symbols-outlined">schedule</span>
              Average Time Between Steps
            </h3>
          </div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>From Step</th>
                  <th>To Step</th>
                  <th>Avg Time (Minutes)</th>
                  <th>Avg Time (Hours)</th>
                  <th>Sample Size</th>
                </tr>
              </thead>
              <tbody>
                {timeAnalysis.map((time, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1 + index * 0.05 }}
                  >
                    <td>
                      <span className="table-badge">{time.from}</span>
                    </td>
                    <td>
                      <span className="table-badge">{time.to}</span>
                    </td>
                    <td className="time-cell">{time.averageTimeMinutes} min</td>
                    <td className="time-cell">{time.averageTimeHours} hrs</td>
                    <td className="sample-cell">{time.sampleSize.toLocaleString()}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          className="table-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
        >
          <div className="card-header">
            <h3>
              <span className="material-symbols-outlined">table_chart</span>
              Funnel Step Details
            </h3>
          </div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Event Name</th>
                  <th>Total Events</th>
                  <th>Unique Users</th>
                  <th>Unique Devices</th>
                  <th>Conversion Rate</th>
                  <th>Drop-off Rate</th>
                  <th>Drop-off Count</th>
                </tr>
              </thead>
              <tbody>
                {funnel.map((step, index) => (
                  <motion.tr
                    key={step.step}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.1 + index * 0.05 }}
                  >
                    <td>
                      <span className="step-badge-small">{step.step}</span>
                    </td>
                    <td className="event-name">{step.eventName}</td>
                    <td>{step.count.toLocaleString()}</td>
                    <td className="users-cell">{step.uniqueUsers.toLocaleString()}</td>
                    <td>{step.uniqueDevices.toLocaleString()}</td>
                    <td>
                      <span className="conversion-badge-cell">{step.conversionRate}%</span>
                    </td>
                    <td>
                      <span className="dropoff-badge-cell">{step.dropOffRate}%</span>
                    </td>
                    <td>{step.dropOffCount.toLocaleString()}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FunnelAnalytics;
